<script setup lang="ts">
import Loader from '@/features/Loader/index';
import AppButton from '@/shared/ui/AppButton/index';
import AppPagination from '@/shared/ui/AppPagination/index';
import LawyerArticleCard from '@/pages/lawyer/ui/sections/ExperienceSection/LawyerArticleCard.vue';
import { useLawyerPublications } from '@/pages/lawyer/composables/useLawyerPublications';
import { PUBLICATIONS_EMPTY_TEXT } from '@/pages/lawyer/lib/publicationsConstants';

const props = withDefaults(defineProps<{ isCompany?: boolean }>(), {
  isCompany: false,
});

const isMobile: boolean = useIsMobile();

const {
  articles,
  publications,
  totalItems,
  currentPage,
  perPage,
  isLoading,
  isShowPagination,
  isShowLoadMore,
  changePage,
  loadMore,
} = useLawyerPublications(props.isCompany);

const isEmpty: ComputedRef<boolean> = computed(
  () => !isLoading.value && totalItems.value === 0,
);

const isFirstLoad: ComputedRef<boolean> = computed(
  () => isLoading.value && totalItems.value === 0,
);
</script>

<template>
  <div
    :class="['publications-block', { _mobile: isMobile }]"
    data-testid="lawyer-publications-block"
  >
    <h3 v-if="!isCompany && !isEmpty && !isFirstLoad" class="publications-block__title">
      Публикации
    </h3>

    <div v-if="isEmpty && !isFirstLoad" class="publications-block__empty">
      {{ PUBLICATIONS_EMPTY_TEXT }}
    </div>

    <ul v-else-if="isCompany && !isFirstLoad" class="publications-block__list _company">
      <li
        v-for="post in publications"
        :key="post.id"
        class="publications-block__item"
        data-testid="lawyer-publication-item"
      >
        <div class="publications-block__item-title">{{ post.name }}</div>
        <div class="publications-block__item-caption">{{ post.description }}</div>
        <a
          class="publications-block__link"
          :href="post.url"
          target="_blank"
          rel="nofollow sponsored ugc noopener"
        >
          Ссылка на публикацию
        </a>
      </li>
    </ul>

    <ul v-else-if="!isFirstLoad" class="publications-block__list">
      <li
        v-for="article in articles"
        :key="article.id"
        class="publications-block__article"
      >
        <LawyerArticleCard :article="article" />
      </li>
    </ul>

    <div
      v-if="isFirstLoad"
      class="publications-block__loading"
      data-testid="lawyer-publications-loader"
    >
      <Loader />
    </div>

    <div v-if="isShowPagination" class="publications-block__pagination">
      <AppPagination
        :model-value="currentPage"
        :per-page="perPage"
        :total-items="totalItems"
        :is-scroll-to-top="false"
        @update:model-value="changePage"
      />
    </div>

    <div v-if="isShowLoadMore" class="publications-block__more">
      <AppButton
        class="publications-block__button"
        button-text="Показать ещё"
        is-big
        :disabled="isLoading"
        @click="loadMore"
      />
    </div>
  </div>
</template>

<style lang="less">
.publications-block {
  .flex-column(@size-x5);

  &__title {
    .h-offset-reset();

    font-size: @size-x6;
    line-height: 25px;
    font-weight: 600;
  }

  &__empty {
    font-weight: 500;
  }

  &__list {
    .flex-column(@size-x5);
  }

  &__list._company {
    gap: 0;
  }

  &__item {
    padding: @size-x5 0;

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }

    &:not(:last-child) {
      border-bottom: 1px solid @color-gallery;
    }
  }

  &__item-title {
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 700;
  }

  &__item-caption {
    font-weight: 400;
    font-size: @size-x4;
    line-height: 22px;
    color: @color-dove-gray;
    margin: 5px 0 10px;
  }

  &__link {
    .color-change-hover();

    display: inline-block;
    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 600;
    text-decoration: underline;
  }

  &__pagination {
    display: flex;
    justify-content: center;
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__more {
    display: flex;
    justify-content: center;
  }

  & &__button._big {
    width: 200px;
  }

  &._mobile &__title {
    font-size: @size-x4;
    line-height: 22px;
  }

  &._mobile &__button._big {
    width: 160px;
  }
}
</style>
